import type { ContentReportResponse } from '@wisecure/api-client';
import type { ModerationContentGroup } from '@/types/dashboard';

const STATUS_PRIORITY: Record<string, number> = {
  PENDING: 0,
  REVIEWED: 1,
  RESOLVED: 2,
  DISMISSED: 3,
};

/**
 * Group flat content reports by content target (reportableType:reportableId).
 *
 * Limitation: the list endpoint paginates the flat report list and only filters
 * by reportableType/status/reason (not reportableId). Grouping is therefore
 * per page. Mitigation: default sortBy 'reportCount', sortOrder 'desc', limit 50
 * so same-target reports cluster together. Same-target reports straddling a page
 * boundary is a rare edge case — acceptable for v1.
 */
export function groupReports(reports: ContentReportResponse[]): ModerationContentGroup[] {
  const map = new Map<string, ModerationContentGroup>();

  for (const report of reports) {
    const key = `${report.reportableType}:${report.reportableId}`;
    const existing = map.get(key);

    if (existing) {
      existing.reporters.push(report);
      if (report.createdAt > existing.latestReportAt) {
        existing.latestReportAt = report.createdAt;
      }
      const priority = STATUS_PRIORITY[report.status] ?? 99;
      const worstPriority = STATUS_PRIORITY[existing.worstStatus] ?? 99;
      if (priority < worstPriority) {
        existing.worstStatus = report.status;
      }
    } else {
      map.set(key, {
        key,
        reportableType: report.reportableType,
        reportableId: report.reportableId,
        reportCount: report.reportCount,
        moderationStatus: report.moderationStatus,
        worstStatus: report.status,
        contentPreview: report.contentPreview,
        reporters: [report],
        latestReportAt: report.createdAt,
      });
    }
  }

  return Array.from(map.values()).sort((a, b) => {
    if (b.reportCount !== a.reportCount) return b.reportCount - a.reportCount;
    return b.latestReportAt.localeCompare(a.latestReportAt);
  });
}
